import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIntroCallRequests from '@/api/useIntroCallRequests.ts';
import useUpdateIntroCallStatus from '@/api/useUpdateIntroCallStatus.ts';
import useCreateReview from '@/api/useCreateReview.ts';
import { Button } from 'react-daisyui';
import dayjs from 'dayjs';
import Routes from '@/router/Routes.ts';
import ReviewModal from '@/components/modals/ReviewModal.tsx';
import { formatCareType } from '@/utils/formatCareType.ts';

const IntroCallDetailsFullPage: FC = () => {
  const { t } = useTranslation();
  const { introCallId } = useParams<{ introCallId: string }>();
  const navigate = useNavigate();
  const { data: requests, isLoading, isError } = useIntroCallRequests();
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateIntroCallStatus();
  const { mutate: createReview, isPending: isSubmittingReview } = useCreateReview();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const request = requests?.find((r) => r.id === introCallId);

  const handleAccept = () => {
    if (request) {
      updateStatus({ request_id: request.id, status: 'accepted' });
    }
  };

  const handleDecline = () => {
    if (request) {
      updateStatus({ request_id: request.id, status: 'declined' });
    }
  };

  const handleMarkCompleted = () => {
    if (request) {
      updateStatus({ request_id: request.id, status: 'completed' });
    }
  };

  const handleSubmitReview = (rating: number, comment: string) => {
    if (request) {
      createReview(
        {
          intro_call_id: request.id,
          reviewee_id: request.carer_id,
          rating,
          comment,
        },
        {
          onSuccess: () => {
            setIsReviewModalOpen(false);
          },
        }
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-[#FFBE00]';
      case 'accepted':
        return 'text-[#00A96E]';
      case 'declined':
        return 'text-[#FF5861]';
      case 'completed':
        return 'text-[#00A96E]';
      case 'not_completed':
        return 'text-[#FF5861]';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_completed':
        return 'Not Completed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <button 
          onClick={() => navigate(Routes.INTRO_CALLS)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <div className="text-red-500">{t('introCallsPage.notFound')}</div>
      </div>
    );
  }

  const showRateButton = request.status === 'completed' && !request.has_review && request.is_requester;
  const showActionButtons = request.status === 'pending' && !request.is_requester;
  const showMarkCompletedButton = request.status === 'accepted' && !request.is_requester;
  
  // Get the other user's ID (the one we want to message)
  const otherUserId = request.is_requester ? request.carer_id : request.user_id;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button 
          onClick={() => navigate(Routes.INTRO_CALLS)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer flex-shrink-0"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <Button
          onClick={() => navigate(Routes.MESSAGES_WITH_USER.replace(':userId', otherUserId))}
          className="bg-black text-white border-none hover:opacity-80 rounded-[12px] px-6 flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          {t('common.sendMessage')}
          <img src="/assets/message.svg" alt="message" className="w-5 h-5" />
        </Button>
      </div>

      <div className="bg-[#F6F6F4] rounded-xl p-4 sm:p-6 space-y-6">
        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">{t('common.status')}</label>
          <div className="bg-white rounded-[120px] mt-2 px-4 py-2 inline-block">
            <p className={`text-sm font-bold ${getStatusColor(request.status)}`}>
              {getStatusLabel(request.status)}
            </p>
          </div>
        </div>

        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">
            {request.is_requester ? 'Carer' : t('tableHeaders.family')}
          </label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
            {request.other_user_name}
          </p>
        </div>

        {request.care_type && (
          <div className="rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('tableHeaders.careType')}</label>
            <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
              {formatCareType(request.care_type)}
            </p>
          </div>
        )}

        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">{t('introCallsPage.dateTimeRequested')}</label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
            {request.preferred_date
              ? `${dayjs(request.preferred_date).format('ddd, DD MMM')} — ${request.preferred_time_from || ''}${request.preferred_time_to ? ` - ${request.preferred_time_to}` : ''}`
              : 'Not specified'}
          </p>
        </div>

        {request.location && (
          <div className="rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('introCallsPage.location')}</label>
            <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
              {request.location}
            </p>
          </div>
        )}

        {request.message && (
          <div>
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('introCallsPage.notesToCarer')}</label>
            <p className="text-sm">{request.message}</p>
          </div>
        )}

        {request.meeting_link && (
          <div className="rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('introCallsPage.introCallLink')}</label>
            <a
              href={request.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-black underline hover:opacity-70 break-all font-semibold"
            >
              {request.meeting_link}
            </a>
          </div>
        )}
      </div>

      {showActionButtons && (
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleAccept}
            disabled={isUpdating}
            className="flex-1 max-w-[160px] bg-[#00A96E] text-white border-none hover:opacity-80 rounded-[12px]"
          >
            {t('common.accept')}
          </Button>
          <Button
            onClick={handleDecline}
            disabled={isUpdating}
            className="flex-1  max-w-[160px] bg-[#FF5861] text-white border-none hover:opacity-80 rounded-[12px]"
          >
            {t('common.decline')}
          </Button>
        </div>
      )}

      {showMarkCompletedButton && (
        <Button
          onClick={handleMarkCompleted}
          disabled={isUpdating}
          className="w-full bg-[#00A96E] text-white border-none hover:opacity-80 rounded-[12px]"
        >
          {t('introCallsPage.markAsCompleted')}
        </Button>
      )}

      {showRateButton && (
        <Button
          onClick={() => setIsReviewModalOpen(true)}
          className="w-full bg-black text-white border-none hover:opacity-80 rounded-[12px]"
        >
          {t('introCallsPage.rateCarer')}
        </Button>
      )}

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSubmit={handleSubmitReview}
        carerName={request.other_user_name}
        isSubmitting={isSubmittingReview}
      />
    </div>
  );
};

export default IntroCallDetailsFullPage;
